import { Box, Typography } from '@mui/material';
import { ChartSpline } from 'lucide-react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import { useIsDoseShown } from '~/hooks/dose/useDoseVisibility';
import { useIntensityRegionController } from '~/hooks/useIntensityRegionController';
import { useLineProfileController } from '~/hooks/useLineProfileController';
import {
    lineProfilePreviewState,
    selectedIntensityRegionState,
    selectedLineProfileState,
} from '~/state/atoms/intensityToolAtoms';
import IntensityRegionDetail from './IntensityRegionDetail';
import LineProfileDetail from './LineProfileDetail';

/** About a region's analysis, so selecting one does not resize the tray. */
const EMPTY_MIN_HEIGHT_PX = 116;

/**
 * The analysis of a profile being placed, as a preview, else of the selected
 * profile or region, or how to get one.
 */
export default function MeasureAnalysis() {
    const intl = useIntl();
    const preview = useRecoilValue(lineProfilePreviewState);
    const profile = useRecoilValue(selectedLineProfileState);
    const region = useRecoilValue(selectedIntensityRegionState);
    const profiles = useLineProfileController();
    const regions = useIntensityRegionController();
    const doseShown = useIsDoseShown();

    if (preview) {
        return (
            <LineProfileDetail
                key="preview"
                profile={preview}
                comparisonForProfile={profiles.comparisonForProfile}
                goTo={profiles.goTo}
            />
        );
    }
    if (profile) {
        return (
            <LineProfileDetail
                key={profile.id}
                profile={profile}
                comparisonForProfile={profiles.comparisonForProfile}
                goTo={profiles.goTo}
            />
        );
    }
    if (region) {
        return (
            <IntensityRegionDetail
                key={region.id}
                region={region}
                image={regions.imageForRegion(region)}
                doseShown={doseShown}
                goTo={regions.goTo}
            />
        );
    }
    return (
        <Box
            data-testid="measure-analysis-empty"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: EMPTY_MIN_HEIGHT_PX,
                boxSizing: 'border-box',
                px: 1.5,
                py: 1.25,
                border: 1,
                borderStyle: 'dashed',
                borderColor: 'divider',
                borderRadius: 1,
                color: 'text.secondary',
            }}
        >
            <ChartSpline size={18} style={{ flexShrink: 0 }} />
            <Typography variant="caption">
                {intl.formatMessage({ id: 'measure.analysis.empty' })}
            </Typography>
        </Box>
    );
}
